import { Component } from '@theme/component';
import { bindPhoneFormatting, formatPhoneNumber } from '@theme/tgf-phone-format';
import { CartLinesUpdateEvent } from '@shopify/events';

// Keep these production keys stable across the YMQ -> native transition.
const keys = { name: 'Dostunuzun adı?', phone: 'Telefon Numaranız?', message: 'Mesajınız?', secondPhone: '2.Telefon Numarası', font: 'İstediğiniz yazı tipi' };
const sizes = ['XSMALL', 'SMALL', 'MEDIUM', 'LARGE'];

export function tagKeys(profile = '') {
  if (!profile.startsWith('legacy-')) return { ...keys, size: 'Lütfen Boy Seçiniz', secondSwitch: '2.bir numara?', fontSwitch: 'Yazı tipi değişikliği?' };
  return {
    size: 'Künye boyunu seçiniz',
    name: profile.endsWith('front') ? 'Ön yüze yazılmasını istediğiniz isim' : 'Arka yüze yazılmasını istediğiniz isim',
    phone: 'Arka yüze yazılmasını istediğiniz telefon numarası',
    secondPhone: 'Arka yüze yazılmasını istediğiniz 2. telefon numarası',
    message: 'Kayboldum Lütfen Ara yerine yazdırmak istediğiniz notu buraya yazabilirsiniz.',
    font: 'Seçtiğiniz yazı tipinin adını yazın',
    secondSwitch: 'İkinci bir telefon numarası daha yazdırmak ister misiniz?',
    fontSwitch: 'Yazı tipini değiştirmek ister misiniz?',
    messageSwitch: 'Arka yüzü için Kayboldum Lütfen Ara yerine başka bir mesaj yazdırmak ister misiniz?'
  };
}

export function tagProperties(size, values, profile = '') {
  if (!sizes.includes(size)) throw new Error('missing-size');
  const fields = tagKeys(profile), legacy = profile.startsWith('legacy-');
  const properties = { [fields.size]: legacy ? size[0] + size.slice(1).toLowerCase() : size };
  for (const field of Object.keys(keys)) {
    const key = fields[field];
    const value = String(values[field] ?? '').trim();
    if (value) properties[key] = field === 'phone' || field === 'secondPhone' ? formatPhoneNumber(value) : value;
  }
  if (properties[fields.secondPhone]) properties[fields.secondSwitch] = 'Evet';
  if (properties[fields.font]) properties[fields.fontSwitch] = 'Evet';
  if (legacy) {
    properties[fields.message] = String(values.message ?? '').trim();
    properties[fields.messageSwitch] = 'Evet';
  }
  return properties;
}

export function tagValuesFromProperties(data, profile = '', editing = false) {
  data = Array.isArray(data) ? Object.fromEntries(data) : (data || {});
  const fields = tagKeys(profile), values = {};
  const size = String(data[fields.size] ?? '').toUpperCase();
  if (sizes.includes(size)) values.size = size;
  for (const field of Object.keys(keys)) {
    const value = data[fields[field]];
    if (typeof value === 'string') values[field] = value;
    else if (editing) values[field] = '';
  }
  if (data[fields.secondSwitch] === 'Hayır') values.secondPhone = '';
  if (data[fields.fontSwitch] === 'Hayır') values.font = '';
  return values;
}

// Existing shared/product URLs can contain one or two URL-encoded JSON layers.
export function tagValuesFromURL(search, profile = '') {
  let raw = new URLSearchParams(search).get('option-value');
  for (let attempt = 0; raw && attempt < 3; attempt++) {
    try {
      const data = JSON.parse(raw);
      if (!data || Array.isArray(data) || typeof data !== 'object') return {};
      return tagValuesFromProperties(data, profile);
    } catch {
      try { raw = decodeURIComponent(raw); } catch { return {}; }
    }
  }
  return {};
}

class TGFTagPersonalizer extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.operation?.abort();
  }

  setup() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.config = JSON.parse(this.querySelector('[data-tag-config]').textContent);
    this.form = this.querySelector('[data-tag-form]');
    this.fields = this.querySelector('[data-tag-fields]');
    this.button = this.querySelector('[data-tag-submit]');
    this.buttonLabel = this.config.available ? this.button.textContent : '';
    this.copy = this.config.copy;
    const initial = this.values || (this.editLine ? tagValuesFromProperties(this.editLine.properties, this.config.profile, true) : tagValuesFromURL(location.search, this.config.profile));
    for (const input of this.querySelectorAll('[data-tag-text]')) {
      const key = input.dataset.tagText;
      if (initial[key] !== undefined) input.value = initial[key];
      if (key === 'phone' || key === 'secondPhone') {
        input.value = formatPhoneNumber(input.value);
        bindPhoneFormatting(input, signal);
      }
    }
    if (initial.size) {
      const radio = [...this.querySelectorAll('[data-tag-size] input')].find(input => input.value === initial.size);
      if (radio) radio.checked = true;
    }
    if (initial.secondPhone || initial.font) this.querySelector('.tgf-tag__extras').open = true;
    this.addEventListener('input', () => { this.clearSuccess(); this.update(); }, { signal });
    this.addEventListener('change', () => {
      this.clearSuccess();
      this.update();
      if (this.size()) this.sizeError(false);
    }, { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); }, { signal });
    this.fields.disabled = !!this.busy;
    this.button.disabled = !this.config.available;
    this.button.hidden = !!this.uncertain;
    this.querySelector('[data-tag-cart]').hidden = !this.uncertain;
    this.update();
  }

  configureEdit(line, buttonLabel) {
    this.editLine = { ...line, properties: Array.isArray(line.properties) ? Object.fromEntries(line.properties) : (line.properties || {}) };
    this.values = undefined;
    this.setup();
    this.button.textContent = buttonLabel;
    this.buttonLabel = buttonLabel;
    this.querySelector('[data-tag-quantity]').value = line.quantity;
  }

  size() { return this.querySelector('[data-tag-size] input:checked')?.value; }

  clearSuccess() {
    if (!this.completed || this.busy || this.uncertain) return;
    this.completed = false;
    this.status();
    this.querySelector('[data-tag-cart]').hidden = true;
  }

  update() {
    this.values = { size: this.size() };
    for (const input of this.querySelectorAll('[data-tag-text]')) this.values[input.dataset.tagText] = input.value;
    let printed = false;
    for (const node of this.querySelectorAll('[data-tag-preview]')) {
      const value = (this.values[node.dataset.tagPreview] || '').trim();
      node.textContent = value;
      node.hidden = !value;
      printed ||= !!value;
    }
    this.querySelector('[data-tag-empty]').hidden = printed;
  }

  sizeError(invalid) {
    const field = this.querySelector('[data-tag-size]');
    const error = this.querySelector('[data-tag-size-error]');
    field.classList.toggle('tgf-tag__size--invalid', invalid);
    error.hidden = !invalid;
    for (const input of field.querySelectorAll('input')) {
      if (invalid) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    }
    if (invalid) {
      field.scrollIntoView({ block: 'center', behavior: 'instant' });
      field.querySelector('input').focus({ preventScroll: true });
    }
  }

  status(text = '', error = false) {
    const status = this.querySelector('[data-tag-status]');
    status.textContent = text;
    status.toggleAttribute('data-error', error);
  }

  async submit() {
    if (this.busy || this.uncertain || !this.config.available) return;
    this.update();
    if (!this.size()) { this.sizeError(true); return; }
    if (!this.form.reportValidity()) return;
    const quantity = Number(this.querySelector('[data-tag-quantity]').value);
    if (!Number.isSafeInteger(quantity) || quantity < 1) return;
    const changed = tagProperties(this.size(), this.values, this.config.profile);
    const properties = { ...(this.editLine?.properties || {}) };
    for (const key of Object.values(tagKeys(this.config.profile))) delete properties[key];
    Object.assign(properties, changed);
    const requestId = crypto.randomUUID();
    properties._tgf_tag_request = requestId;
    this.busy = true;
    this.fields.disabled = true;
    this.button.textContent = this.editLine ? this.copy.saving : this.copy.adding;
    this.status();
    this.operation = new AbortController();
    let posted = false;
    try {
      const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId))].slice(0, 5);
      const item = { id: this.config.variantId, quantity, properties };
      if (this.editLine) {
        const latestResponse = await fetch(`${window.Shopify.routes.root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(12000) });
        if (!latestResponse.ok) throw new Error();
        const latest = await latestResponse.json();
        const current = latest.items.find(line => line.key === this.editLine.key);
        if (!current || Number(current.variant_id) !== Number(item.id) || current.quantity !== this.editLine.quantity) throw new Error();
      }
      posted = true;
      const response = await fetch(`${window.Shopify.routes.root}cart/${this.editLine ? 'change' : 'add'}.js`, {
        method: 'POST', credentials: 'same-origin',
        signal: AbortSignal.any([this.operation.signal, AbortSignal.timeout(15000)]),
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(this.editLine ? { id: this.editLine.key, quantity, properties, sections, sections_url: location.pathname } : { items: [item], sections, sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok) {
        if (response.status >= 400 && response.status < 500) posted = false;
        throw new Error(this.copy.error);
      }
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`, {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' },
        signal: AbortSignal.any([this.operation.signal, AbortSignal.timeout(12000)])
      });
      if (!cartResponse.ok) throw new Error(this.copy.uncertain);
      const cart = await cartResponse.json();
      const added = cart.items.find(line => line.properties?._tgf_tag_request === requestId);
      if (!added || Number(added.variant_id) !== Number(item.id) || added.quantity !== quantity ||
          Object.entries(properties).some(([key, value]) => added.properties[key] !== value)) throw new Error(this.copy.uncertain);
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: this.editLine ? 'update' : 'add', context: this.editLine ? 'cart' : 'product', lines: [{ merchandiseId: String(item.id), quantity }], promise: deferred.promise }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, sections: result.sections, source: 'tgf-tag-personalizer', itemCount: quantity, didError: false } });
      this.completed = true;
      this.status(this.editLine ? this.copy.saved : this.copy.success);
      this.querySelector('[data-tag-cart]').hidden = false;
      if (this.editLine) this.dispatchEvent(new CustomEvent('tgf-tag-saved', { bubbles: true }));
      else document.querySelector('cart-drawer-component')?.closest('theme-drawer')?.open();
    } catch {
      if (posted) {
        this.uncertain = true;
        this.button.hidden = true;
        this.querySelector('[data-tag-cart]').hidden = false;
      }
      if (this.isConnected) this.status(posted ? this.copy.uncertain : this.copy.error, true);
    } finally {
      this.busy = false;
      this.fields.disabled = false;
      this.button.textContent = this.buttonLabel;
    }
  }
}

if (!customElements.get('tgf-tag-personalizer')) customElements.define('tgf-tag-personalizer', TGFTagPersonalizer);
